import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { fmtMoney } from "@/lib/money";
import { FileText, Trash2, Download, Search } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { pdfStr, isRTL } from "@/lib/pdfStrings";
import { PdfLangMenu } from "@/components/PdfLangMenu";

export default function Historial() {
  const { api, user } = useAuth();
  const { t, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/quotes").then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/quotes/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const downloadPDF = async (item, langOverride = null) => {
    const activeLang = langOverride || lang;
    const S = pdfStr(activeLang);
    const best = item.resultados?.[0];
    if (!best) return toast.error("Sin resultados");
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const w = doc.internal.pageSize.getWidth();
    if (activeLang === "ar" || [item.origen, item.destino, best.carrier, best.servicio].some(hasArabic)) {
      await ensureArabicFont(doc);
    }
    const img = new Image();
    img.onload = () => { try { doc.addImage(img, "PNG", 40, 30, 80, 55); } catch (e) { console.warn("Historial logo embed failed:", e); } render(); };
    img.onerror = render;
    img.src = "/logo.png";
    function render() {
      doc.setFont("helvetica","bold"); doc.setFontSize(18); doc.setTextColor(185,28,28);
      doc.text("MEOS CARGO", w - 40, 55, { align: "right" });
      doc.setFontSize(9); doc.setTextColor(100,116,139);
      doc.setFont("helvetica","normal");
      pdfText(doc, S.quote_title, w - 40, 72, { align: "right" });
      doc.text(new Date(item.created_at).toLocaleString(), w - 40, 86, { align: "right" });

      let y = 130;
      doc.setDrawColor(226,232,240); doc.line(40, y, w - 40, y);
      y += 26;
      doc.setFont("helvetica","bold"); doc.setFontSize(14); doc.setTextColor(15,23,42);
      pdfText(doc, `${item.origen} → ${item.destino}`, 40, y);
      y += 18; doc.setFontSize(10); doc.setFont("helvetica","normal"); doc.setTextColor(100,116,139);
      const modeKey = { aereo: "mode_aereo", terrestre: "mode_terrestre", maritimo: "mode_maritimo" };
      const modeLabel = S[modeKey[item.mode]] || (item.mode || "").toUpperCase();
      pdfText(doc, `${modeLabel} · ${(item.tipo_carga||"").toUpperCase()}`, 40, y);

      y += 30; doc.setFontSize(11); doc.setFont("helvetica","bold"); doc.setTextColor(15,23,42);
      pdfText(doc, best.carrier, 40, y);
      doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(100,116,139);
      pdfText(doc, `${best.servicio} · ${best.dias} ${S.days}`, 40, y + 14);

      y += 50;
      const d = best.desglose || {}; const cur = best.moneda || "USD";
      const rowsD = [
        [S.base_price, fmtMoney(d.precio_base, cur)],
        [`${d.cantidad} × ${fmtMoney(d.precio_unitario, cur)} / ${d.unidad}`, fmtMoney(d.subtotal_variable, cur)],
        [`${S.margin} (${d.margen_pct}%)`, fmtMoney(d.margen_monto, cur)],
        [S.waybill_fee, fmtMoney(d.cargo_guia, cur)],
      ];
      doc.setFontSize(10); doc.setFont("helvetica","bold"); doc.setTextColor(15,23,42);
      pdfText(doc, S.subtotal.toUpperCase(), 40, y);
      y += 8; doc.line(40, y, w - 40, y);
      doc.setFont("helvetica","normal");
      rowsD.forEach(([l, v]) => {
        y += 18;
        doc.setTextColor(71,85,105); pdfText(doc, String(l), 40, y);
        doc.setTextColor(15,23,42); doc.text(String(v), w - 40, y, { align: "right" });
      });
      y += 22; doc.setDrawColor(185,28,28); doc.setLineWidth(1.5); doc.line(40, y, w - 40, y);
      y += 22; doc.setFont("helvetica","bold"); doc.setFontSize(14); doc.setTextColor(15,23,42);
      pdfText(doc, S.total.toUpperCase(), 40, y);
      doc.setTextColor(185,28,28);
      doc.text(`${fmtMoney(best.precio, cur)}  ${cur}`, w - 40, y, { align: "right" });

      doc.setFontSize(8); doc.setTextColor(148,163,184);
      pdfText(doc, S.footer_tagline, w/2, 800, { align: "center" });
      doc.save(`MeosCargo_${item.origen}_${item.destino}.pdf`);
      toast.success("PDF");
    }
  };

  const filtered = rows.filter(r => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.origen||"").toLowerCase().includes(s) ||
           (r.destino||"").toLowerCase().includes(s) ||
           (r.user_email||"").toLowerCase().includes(s) ||
           (r.user_name||"").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4" data-testid="historial-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><FileText className="w-5 h-5 text-red-600" /> {t("history")}</h2>
          <p className="text-sm text-slate-500">{filtered.length} {t("quotes").toLowerCase()}</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute top-2.5 start-3 text-slate-400" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder={t("search")} className="ps-9 pe-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 w-64" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400">
          <FileText className="w-14 h-14 mx-auto opacity-30 mb-3" />
          <p>{t("no_history")}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("date_col")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("route_col")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("mode_col")}</th>
                <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("cargo_col")}</th>
                {["admin","ventas"].includes(user.role) && <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("client")}</th>}
                <th className="p-3 text-end text-[11px] uppercase tracking-wider font-semibold">{t("best_price_col")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const best = r.resultados?.[0];
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hist-${r.id}`}>
                    <td className="p-3 text-slate-600 text-xs tabular-nums">{new Date(r.created_at).toLocaleDateString()} <span className="text-slate-400">{new Date(r.created_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</span></td>
                    <td className="p-3 font-medium text-slate-900">{r.origen} → {r.destino}</td>
                    <td className="p-3 text-slate-600 text-xs uppercase">{r.mode}</td>
                    <td className="p-3 text-slate-600 text-xs uppercase">{r.tipo_carga}</td>
                    {["admin","ventas"].includes(user.role) && <td className="p-3 text-slate-600 text-xs">{r.user_name || r.user_email}</td>}
                    <td className="p-3 text-end font-semibold tabular-nums">{best ? fmtMoney(best.precio, best.moneda) : "—"}</td>
                    <td className="p-3 text-end space-x-1">
                      {best && (
                        <PdfLangMenu testid={`hist-pdf-${r.id}`} compact icon={<Download className="w-4 h-4" />} onPrint={(lg)=>downloadPDF(r, lg)} />
                      )}
                      <button onClick={() => remove(r.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
